/* eslint-disable @typescript-eslint/ban-types */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { Action10061Options } from '@hfzq/crab-utils/esm/utils/navigation/action10061';
import { globalConfig } from '@src/config';
import { compilePath } from '@src/helper/tools';
import React, { ComponentType, forwardRef, Ref, Suspense, useEffect } from 'react';
import { RouteObject } from 'react-router-dom';
import { uuid } from '@hfzq/crab-utils';
import { generateXFNUrl, navigate, replace, fullscreen } from './navigate';

const joinPaths = (paths: string[]): string => paths.join('/').replace(/\/\/+/g, '/');

type LazyElementProps<T> = {
  load: () => Promise<{ default: ComponentType<T> }>;
  loading?: React.ReactNode;
} & T;

export const LazyElement = forwardRef(({ load, loading, ...props }: any, ref: Ref<unknown>) => {
  const [element, setElement] = React.useState<React.ReactNode>(null);
  const ele = React.useMemo(() => {
    const LazyEl = React.lazy(load);
    return (
      <Suspense fallback={loading}>
        <LazyEl {...props} ref={ref} />
      </Suspense>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [load]);
  useEffect(() => {
    setElement(ele);
  }, [ele]);
  return <>{element}</>;
}) as <T>(p: LazyElementProps<T> & { ref?: Ref<unknown> }) => JSX.Element;

type QueryParamsValue = string | number | boolean;
export type QueryParams = Record<string, QueryParamsValue | QueryParamsValue[]>;
type QueryValuePass<T> = {
  [P in keyof T]: T[P] extends any[] ? T[P] | string[] : T[P] | string;
};

type RouterConfigNavigateOptions<Q extends QueryParams> = {
  query: QueryValuePass<Q>;
  webviewConfig?: Action10061Options;
};

type QueryType<Q extends QueryParams> = keyof ExcludePartial<Q> extends never
  ? Partial<Pick<RouterConfigNavigateOptions<Q>, 'query'>>
  : Pick<RouterConfigNavigateOptions<Q>, 'query'>;

type NavigateParams<Q extends QueryParams> = keyof ExcludePartial<Q> extends never
  ? void | Partial<RouterConfigNavigateOptions<Q>>
  : QueryType<Q> & Omit<RouterConfigNavigateOptions<Q>, 'query'>;

type GetCurrentUrlFuncParams<Q extends QueryParams> = keyof Q extends never ? QueryParams | void : Q;

// 扩展react router config 配置
type RouteObjectItem = RouteObject & {
  path: string;
  name?: string;
  webviewConfig?: Action10061Options;
  children?: RouteObjectItem[];
  // 页面跳转之前执行
  // 可用于文件的预加载
  beforeNavigate?: () => void | Promise<void>;
};

/**
 * 管理路由配置
 */
export class RouterConfigManager {
  private routesConfig: RouteObjectItem[] = [];

  private basename = '/';

  /**
   * 初始化配置
   *
   * @param options
   */
  constructor(options?: { basename?: string }) {
    this.basename = options?.basename?.replace(/^\./, '') || this.basename;
  }

  /**
   * 获取当前路由配置的path
   * @param config
   */
  private getCurrentPath(config: RouteObjectItem) {
    let currentPath = '';
    // eslint-disable-next-line consistent-return
    const recursive = (parentConfig: RouteObjectItem[], path: string[] = []): string => {
      // eslint-disable-next-line no-restricted-syntax
      for (const item of parentConfig) {
        path.push(item.path?.startsWith('/') ? item.path : `/${item.path}`);
        if (item.children) {
          recursive(item.children, path);
        }
        if (item === config) {
          currentPath = path.join('');
          return currentPath;
        }
        path.pop();
      }
      return '';
    };
    recursive(this.routesConfig);

    return currentPath || '/';
  }

  /**
   * 生成完整的路由地址
   *
   * @param path 路由地址
   */
  private getFullPath(path: string) {
    return joinPaths([this.basename, path]);
  }

  /**
   * 处理跳转参数配置
   *
   * @param currentPath 当前路由path
   * @param options 跳转参数
   */
  private handleNavigateOptions<Q extends QueryParams = {}>(config: RouteObjectItem, options: NavigateParams<Q>) {
    const configOptions = (options || {}) as RouterConfigNavigateOptions<Q>;
    const currentPath = this.getCurrentPath(config);
    const { path, search } = compilePath(currentPath, configOptions.query);

    const url = this.getFullPath(path) + search;
    return {
      path,
      search,
      url,
      webviewConfig: { ...config.webviewConfig, ...configOptions.webviewConfig },
    };
  }

  /**
   * 获取路由配置
   */
  public getRoutesConfig() {
    return this.routesConfig;
  }

  /**
   * 创建路由
   *
   * @param config
   */
  public createRoute<Q extends QueryParams = {}>(config: RouteObjectItem, parentConfigs = this.routesConfig) {
    // 修复LazyElement路由切换时内部状态无法更新的问题
    if (React.isValidElement(config.element)) {
      // eslint-disable-next-line no-param-reassign
      config.element = React.cloneElement(config.element, { key: uuid() });
    }
    parentConfigs.push(config);
    return {
      config,
      navigate: async (options: NavigateParams<Q>) => {
        const { url, webviewConfig } = this.handleNavigateOptions(config, options);
        await config.beforeNavigate?.();
        navigate(url, webviewConfig);
      },
      replace: async (options: NavigateParams<Q>) => {
        const { url, webviewConfig } = this.handleNavigateOptions(config, options);
        await config.beforeNavigate?.();
        replace(url, webviewConfig);
      },
      fullscreen: async (options: NavigateParams<Q>) => {
        const { url, webviewConfig } = this.handleNavigateOptions(config, options);
        await config.beforeNavigate?.();
        fullscreen(url, webviewConfig);
      },
      createRoute: <ChildQuery extends QueryParams = {}>(childConfig: RouteObjectItem) => {
        if (!config.children) {
          // eslint-disable-next-line no-param-reassign
          config.children = [];
        }
        return this.createRoute<ChildQuery & Q>(childConfig, config.children);
      },
      getCurrentUrl: (params: GetCurrentUrlFuncParams<Q>) => {
        const currentPath = this.getCurrentPath(config);
        const { search, path } = compilePath(currentPath, params || undefined);
        const fullPath = this.getFullPath(path);
        const { location } = window;
        return {
          search,
          realPath: fullPath,
          path: generateXFNUrl(fullPath),
          origin: location.origin,
          sharePath: `/index.html#${fullPath + search}`,
          appOpenPath: `/fund-mall/index.html#${fullPath + search}`,
        };
      },
    };
  }
}

export type RouterItem = ReturnType<typeof routerConfigManager.createRoute>;

export const routerConfigManager = new RouterConfigManager({ basename: globalConfig.basename });
